// Badges de TYPE d'artefact (cahier §3.3.8, §3.5, P4) : le serveur nomme le type du visuel,
// seule l'interface connaît sa teinte (jetons --ds-art-<type>-*) et son icône. Module léger,
// du bundle initial (skeleton dès `artifact_pending`, en-tête du panneau d'artefacts).
import { Badge } from '../Badge';
import {
  ActivityIcon, AreaChartIcon, CalendarIcon, CameraIcon, ChartIcon, DonutChartIcon, FileTextIcon, GaugeIcon, GlobeIcon,
  GroupedBarIcon, HeatmapIcon, HorizontalBarIcon, LineChartIcon, MapIcon, PieChartIcon, RankingIcon, RouteIcon,
  ScatterIcon, ShieldCheckIcon, StackedBarIcon, TableIcon, WaterfallIcon,
} from '../icons';
import { ART_TYPES, documentArtType } from './artTypes.js';
import { TYPE_LABELS } from './visualTypes.js';

export { ART_TYPES, documentArtType };

// Une icône par type de visuel : avec la teinte, elle distingue les types proches
// (histogramme et PowerPoint orangés, jauge et classement dorés) pour les daltonismes
export const TYPE_ICONS = {
  line: LineChartIcon,
  area: AreaChartIcon,
  bar: ChartIcon,
  stacked_bar: StackedBarIcon,
  horizontal_bar: HorizontalBarIcon,
  grouped_horizontal_bar: GroupedBarIcon,
  pie: PieChartIcon,
  donut: DonutChartIcon,
  gauge: GaugeIcon,
  kpi_cards: ActivityIcon,
  reliability_cards: ShieldCheckIcon,
  ranking: RankingIcon,
  heatmap: HeatmapIcon,
  scatter: ScatterIcon,
  waterfall: WaterfallIcon,
  table: TableIcon,
  markers: MapIcon,
  heat: MapIcon,
  zones: MapIcon,
  route: RouteIcon,
  trace: RouteIcon,
  planning: CalendarIcon,
  embed: GlobeIcon,
  photos: CameraIcon,
};

/**
 * Badge du type d'un visuel (carte, skeleton) : teinte et icône du type, libellé français.
 * Type inconnu (annonce encore sans type) : badge de type au ton primaire, icône générique.
 */
export function TypeBadge({ visualType, label = null, icon = null, className = '' }) {
  return (
    <Badge type={ART_TYPES[visualType] ?? true} icon={icon ?? TYPE_ICONS[visualType] ?? ChartIcon} className={className}>
      {label ?? TYPE_LABELS[visualType] ?? 'Visuel'}
    </Badge>
  );
}

/**
 * Badge de l'en-tête du panneau d'artefacts : {type, icon, label} selon l'artefact (type du
 * visuel affiché, carte, planning, page web, format du document, tableau), ou null pour
 * les artefacts sans teinte propre (code, fichier, raisonnement : libellé et icône du
 * registre, ton primaire).
 */
export function panelBadge(artifact) {
  const payload = artifact?.payload ?? {};
  switch (artifact?.type) {
    case 'visual': {
      const view = payload.viewType ?? payload.spec?.type ?? null;
      return { type: ART_TYPES[view] ?? true, icon: TYPE_ICONS[view] ?? ChartIcon, label: TYPE_LABELS[view] ?? 'Visuel' };
    }
    case 'map': {
      const view = payload.viewType ?? payload.spec?.visual_type ?? null;
      return { type: 'carte', icon: TYPE_ICONS[view] ?? MapIcon, label: TYPE_LABELS[view] ?? 'Carte' };
    }
    case 'planning':
      return { type: 'planning', icon: CalendarIcon, label: 'Planning' };
    case 'embed':
      return { type: 'web', icon: GlobeIcon, label: 'Page web' };
    case 'document':
      return { type: documentArtType(payload.spec?.format ?? payload.spec?.file?.format) ?? true, icon: FileTextIcon, label: 'Document' };
    case 'table':
      return { type: 'table', icon: TableIcon, label: 'Tableau' };
    default:
      return null;
  }
}
